// src/components/instructor/ops/ScheduleVisibilityControl.js
//
// Pass 57A/58D — real admin control for instructor schedule visibility. Two modes:
//   mode="organization" → org-level default policy (organizations/{orgId}.scheduleVisibilityPolicy)
//   mode="member"        → per-instructor override on the org relationship (organizationMembers doc)
// Collective instructors are self-managed: the selector is disabled and shows the exemption.
//
// Pass 58D: the browser NEVER directly writes a rolling policy. EVERY change goes through the DEPLOYED
// atomic callable `updateScheduleVisibilityPolicy`, which writes the policy AND recomputes the affected
// bookings' instructorVisibleFromMs in one operation — so the queryable/rule state can never go stale.
// The UI shows real Saving / Saved / server-error states and never claims success before the callable
// resolves.

import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../firebase';
import { useAuth } from '../../../AuthContext';
import { authorizeOrgWrite } from '../../../utils/operationalWriteGate';
import { VISIBILITY_PRESETS, effectiveInstructorScheduleVisibility, resolveOrganizationVisibilityPolicy } from '../../../utils/scheduleVisibility';
import { resolveInstructorCategory, CATEGORY, CURRENT_SEASON_ID } from '../../../utils/instructorCategory';
import { memberDocId } from '../../../utils/booking';

const ORG_OPTIONS = VISIBILITY_PRESETS; // 7/14/30/60/90/full_season
const MEMBER_OPTIONS = [{ key: 'default', label: 'Use organization default', horizonDays: undefined }, ...VISIBILITY_PRESETS];

// The ONE authoritative policy+recalc endpoint (deployed Cloud Function). Never a direct client write.
function callUpdatePolicy(payload) {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'updateScheduleVisibilityPolicy');
  return fn(payload);
}

export default function ScheduleVisibilityControl({ mode = 'organization', organization: organizationProp = null, organizationId, member = null, seasonId = CURRENT_SEASON_ID }) {
  const { userData } = useAuth() || {};
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [orgDoc, setOrgDoc] = useState(organizationProp);
  const [savedMemberKey, setSavedMemberKey] = useState(null);
  const organization = organizationProp || orgDoc || {};
  const canManage = authorizeOrgWrite({ userData, organizationId, action: 'team_access' }).ok;

  // Self-load the current org policy when not supplied, so the selector reflects the saved value.
  useEffect(() => {
    let alive = true;
    if (!organizationProp && organizationId) {
      getDoc(doc(db, 'organizations', organizationId)).then((s) => { if (alive && s.exists()) setOrgDoc(s.data()); }).catch(() => {});
    }
    return () => { alive = false; };
  }, [organizationProp, organizationId]);

  if (mode === 'member' && member) {
    const category = resolveInstructorCategory(member, { season: seasonId }) === CATEGORY.UNCLASSIFIED
      ? resolveInstructorCategory(member, {}) : resolveInstructorCategory(member, { season: seasonId });
    if (category === CATEGORY.COLLECTIVE) {
      return <span style={{ fontSize: 12, color: '#6B7280' }} data-testid="visibility-collective">Calendar visibility: <strong>Self-managed (Collective)</strong></span>;
    }
    const current = savedMemberKey || member.scheduleVisibilityOverride || 'default';
    const eff = effectiveInstructorScheduleVisibility({ organization, member, seasonId });
    const onChange = async (e) => {
      const v = e.target.value; setMsg('');
      if (!authorizeOrgWrite({ userData, organizationId, action: 'team_access' }).ok) { setMsg('Not authorised'); return; }
      setBusy(true); setMsg('Saving…');
      try {
        const id = member.id || memberDocId(member.userId, organizationId);
        // Server performs the override + per-booking recalc atomically. No direct client write.
        await callUpdatePolicy({ organizationId, scope: 'member', memberDocId: id, choiceKey: v, seasonId });
        setSavedMemberKey(v); setMsg('Saved.');
      } catch (err) { setMsg(err?.message || 'Server error — not saved.'); } finally { setBusy(false); }
    };
    return (
      <span style={{ fontSize: 12 }}>
        Calendar visibility:{' '}
        <select value={current} disabled={!canManage || busy} onChange={onChange} data-testid="visibility-member-select">
          {MEMBER_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>{' '}
        <em style={{ color: '#6B7280' }}>({eff.mode === 'full_season' ? 'full season' : `${eff.horizonDays}d`}{eff.source === 'member_override' ? ', override' : ', org default'})</em>
        {msg && <span style={{ marginLeft: 6, color: '#6B7280' }} data-testid="visibility-msg">{msg}</span>}
      </span>
    );
  }

  // organization-level default
  const org = resolveOrganizationVisibilityPolicy(organization);
  const currentKey = org.mode === 'full_season' ? 'full_season' : String(org.horizonDays);
  const onChangeOrg = async (e) => {
    const key = e.target.value; setMsg('');
    if (!authorizeOrgWrite({ userData, organizationId, action: 'team_access' }).ok) { setMsg('Not authorised'); return; }
    setBusy(true); setMsg('Saving…');
    try {
      // Server writes the org policy AND recomputes every affected School booking's instructorVisibleFromMs.
      await callUpdatePolicy({ organizationId, scope: 'organization', choiceKey: key, seasonId });
      // Reflect the saved policy locally so the selector shows the new value.
      setOrgDoc((prev) => ({ ...(prev || {}), scheduleVisibilityPolicy: key === 'full_season' ? { mode: 'full_season', horizonDays: null } : { mode: 'rolling', horizonDays: ORG_OPTIONS.find((o) => o.key === key)?.horizonDays } }));
      setMsg('Saved.');
    } catch (err) { setMsg(err?.message || 'Server error — not saved.'); } finally { setBusy(false); }
  };
  return (
    <div style={{ fontSize: 13, padding: '8px 0' }} data-testid="visibility-org">
      <strong>Schedule visibility for organization instructors</strong>{' '}
      <select value={currentKey} disabled={!canManage || busy} onChange={onChangeOrg} data-testid="visibility-org-select">
        {ORG_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      {!canManage && <span style={{ marginLeft: 8, color: '#9CA3AF' }}>(owner/admin only)</span>}
      {msg && <div style={{ color: '#6B7280', marginTop: 4 }} data-testid="visibility-msg">{msg}</div>}
      <div style={{ color: '#9CA3AF', marginTop: 2 }}>Applies to SAS School instructors. Collective scheduling uses its separate source when connected.</div>
    </div>
  );
}
